import { useMemo } from 'react';
import { Platform } from 'react-native';

import { useSession } from '@/components/auth';
import { useApi } from '@/hooks/use-api';
import { useAssociations } from '@/hooks/use-data';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { FeatureFlagName, UserRole } from '@/types/models';

export interface Permissions {
	// Navigation permissions
	canViewReports: boolean;
	canViewUsers: boolean;
	canViewMaterials: boolean;
	canViewAdmin: boolean;
	canViewInspections: boolean;

	// Association permissions
	canEditAssociations: boolean;
	canCreateAssociations: boolean;
	canDeleteAssociations: boolean;
	canEditAssociationNotes: boolean;

	// Work order permissions
	canEditWorkOrders: boolean;
	canCreateWorkOrders: boolean;
	canDeleteWorkOrders: boolean;
	/**
	 * Correct (edit/delete) a work order's cost lines — time entries, materials, misc costs.
	 * The server's DELETE_TIME_ENTRIES / DELETE_MATERIALS / DELETE_MATERIAL_COSTS /
	 * DELETE_MISC_COSTS resolve to exactly the `canDeleteWorkOrders` set for every role, with and
	 * without `work_order_admin`; `tests/api/permissions.capabilities.spec.ts` pins that.
	 */
	canDeleteCostLines: boolean;
	/** Holds `work_order_admin` (the Admin role counts). Also unlocks the Date of Order correction. */
	isWorkOrderAdmin: boolean;
	// Narrow people-write exception: fill a matched account's owner contact from
	// a work order (set a missing phone / create the missing owner). Mirrors the
	// server's MANAGE_WORK_ORDER_OWNER_CONTACT grant (everyone but contractors).
	canManageOwnerContact: boolean;

	// Exterior improvement application permissions (#363)
	/** The unified inbox — per-user feature flag, web only. */
	canUseInbox: boolean;

	// User management permissions
	canEditUsers: boolean;
	canCreateUsers: boolean;
	canDeleteUsers: boolean;
	/**
	 * Edit or delete a task SOMEONE ELSE created — `canManageOthersTasks` in api/tasks.ts, which
	 * is `UPDATE_USERS`: the Admin role or `user_admin`. Not gated on demo mode, unlike the Users
	 * page, because tasks are not local-only.
	 */
	canManageOthersTasks: boolean;

	// Admin permissions
	canAccessAdmin: boolean;
	/**
	 * Holds at least one CORE admin capability: the super-administrator role, or one of
	 * `user_admin` / `association_admin` / `work_order_admin`.
	 *
	 * The operational admin tools — Activity History, Import Batches, Location Analytics,
	 * Documents Admin — read across the whole org but destroy nothing, so they belong to
	 * this set rather than to `canAccessAdmin` (which is the super-admin role alone and
	 * guards Backup, Restore and the SQL console).
	 *
	 * Deliberately NOT "holds any flag at all". `email_templates_admin` or
	 * `cheat_sheet_admin` on its own is authority over WORDING, and should not confer the
	 * org-wide audit log or staff location history. Contractors are excluded, since all
	 * three source booleans already are.
	 */
	hasAdminCapability: boolean;
	canManageDB: boolean;
	/**
	 * Reshape the document store (hide a root, set a folder icon, pin a community to a
	 * drive folder, onboard a community from its folder) -- the client mirror of
	 * `Permission.MANAGE_DOCUMENT_STRUCTURE`: the Admin role, or `association_admin`.
	 */
	canManageDocumentStructure: boolean;
	/**
	 * Operate the ONE shared document store — connect or disconnect Dropbox (Settings →
	 * Integrations), provision an association's folder tree, archive, reindex. The client mirror
	 * of `hasDocumentOperatorCapability` in api/permissions.ts: the Admin role, or `user_admin` /
	 * `association_admin`. Never a contractor. Not `work_order_admin`: rebuilding the company's
	 * file store is not work-order administration.
	 */
	isDocumentOperator: boolean;
	/**
	 * Holds `UPDATE_ACCOUNTS` — the accounting WRITE set: the Admin role, the Accounting role, or
	 * `accounting_admin` together with its `accounting` prerequisite. The same answer as
	 * `useAccountingPermissions().canModify`, exposed here for money fields on non-accounting
	 * screens (a time entry's rate, a material's unit cost, re-attributing labour).
	 */
	canUpdateAccounts: boolean;
	/**
	 * Holds the `association_admin` capability (the Admin role counts). Besides the association
	 * writes, it is what lifts a property manager's "their own associations" scope when picking,
	 * moving or listing work orders — mirroring `isPmUnscoped` in api/work-orders.ts.
	 */
	isAssociationAdmin: boolean;
	canViewSettings: boolean;

	/**
	 * The two work-order queues on the v2 sidebar, gated by WHO ACTS on them.
	 *
	 * Needs Review is accounting's step (writing the customer-facing description), and the
	 * Admin role can stand in. Needs Approval is the property manager's step; maintenance and
	 * contractors never approve, so the row would only 403 for them. An admin sees both.
	 */
	canViewReviewQueue: boolean;
	canViewApprovalQueue: boolean;

	// Special flags
	isAdmin: boolean;
	isPropertyManager: boolean;
	isSupport: boolean;
	isMaintenance: boolean;
	isContractor: boolean;
	isAccounting: boolean;
	/**
	 * True while the per-user capability flags are still loading (#500).
	 *
	 * Role-derived booleans are known synchronously from the session. Capability-derived
	 * ones — `canViewUsers` for a `user_admin` holder — are FALSE until `/me/feature-flags`
	 * answers. A route guard that redirects on a false it reads during that window sends a
	 * fully-entitled user home before their entitlement has arrived; wait on this first.
	 */
	isLoading: boolean;

	// Helper methods
	getAssignedAssociationIds: () => string[];
}

/**
 * The Inbox (#363, #369) is a WEB-ONLY workflow. The label rail, the Gmail-style
 * reading pane and the sandboxed HTML email renderer are all built for a large
 * screen, and the renderer has no native implementation (`react-native-webview`
 * ships no web build, so the two would have to diverge).
 *
 * Gating the permission flags rather than just the sidebar turns the whole
 * feature off in one place: the nav item, its unread badge query, and every route
 * guard already key off these, so deep links redirect out too.
 */
const INBOX_PLATFORM_SUPPORTED = Platform.OS === 'web';

/**
 * The roles whose own matrix row already grants `DELETE_WORK_ORDERS`.
 *
 * A MIRROR of `PERMISSION_MATRIX` in api/permissions.ts, where the permission is true in
 * `DEFAULT_PERMISSIONS` and switched off only for Property Manager, Maintenance and
 * Contractor — so Accounting and Support inherit it. Listed here rather than resolved through
 * `hasPermission` on purpose: that function lives in `api/` beside D1 queries and `Response`
 * builders, and the app deliberately keeps server code out of its bundle (see the docblock in
 * utils/permission-matrix.ts). `tests/api/permissions.capabilities.spec.ts` asserts the four
 * role verdicts this list encodes, so the matrix cannot move without a red test.
 *
 * The `work_order_admin` capability EXTENDS delete to roles the matrix denies. It must never
 * withdraw it from a role that already holds it: a render guard stricter than the write guard
 * is the exact bug class this whole change exists to remove — the button would be hidden from
 * someone whose DELETE the server accepts.
 */
const ROLES_WITH_WORK_ORDER_DELETE: readonly (UserRole | undefined)[] = [
	UserRole.Accounting,
	UserRole.Support,
];

export function usePermissions(): Permissions {
	const { user } = useApi();
	const { session } = useSession();
	const isDemoMode = session?.isDemoMode ?? false;
	/**
	 * The Inbox gate IS the per-user feature flag, toggled from the user editor.
	 *
	 * One flag rather than the four granular can-view/create/update/delete booleans this
	 * used to carry: they were always the same value, and the SERVER enforces the real
	 * per-role permissions on every route — a second, coarser copy in the client only
	 * created two places to disagree.
	 *
	 * Still ANDed with the platform check: turning the flag on for someone does not make
	 * the native reading pane exist. Defaults OFF while flags load, so the nav item cannot
	 * flash in and then vanish.
	 */
	const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
	const canUseInbox = INBOX_PLATFORM_SUPPORTED && hasFlag(FeatureFlagName.Inbox);

	// Use a single associations query with caching
	const { data: associationsData } = useAssociations({
		status: 'active',
		limit: 1000,
	});

	// Memoize the assigned association IDs to avoid recalculating on every render
	const assignedAssociationIds = useMemo(() => {
		console.log('usePermissions - Calculating assigned association IDs:', {
			user: user ? { id: user.id, role: user.role } : null,
			associationsData: associationsData ? {
				dataLength: associationsData.data?.length,
				firstFew: associationsData.data?.slice(0, 3).map(a => ({ id: a.id, name: a.name, assignedTo: a.assignedTo })),
			} : null,
		});

		if (!user || !associationsData?.data) return [];

		// The Admin ROLE sees all associations (#500 — the retired is_admin column no longer does).
		if (user.role === UserRole.Admin) {
			return associationsData.data.map(a => a.id);
		}

		// Property Managers see only associations assigned to them
		if (user.role === UserRole.PropertyManager) {
			const assigned = associationsData.data
				.filter(a => a.assignedTo === user.id)
				.map(a => a.id);
			console.log('usePermissions - Property Manager assigned associations:', {
				userId: user.id,
				assignedAssociationIds: assigned,
				allAssociations: associationsData.data.map(a => ({ id: a.id, name: a.name, assignedTo: a.assignedTo })),
			});
			return assigned;
		}

		// Other roles see all associations (for now)
		return associationsData.data.map(a => a.id);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- associationsData?.data is the intentional dep; ESLint wants full associationsData object which would over-fire
	}, [user, associationsData?.data]);

	// Helper function to get assigned association IDs
	const getAssignedAssociationIds = (): string[] => {
		return assignedAssociationIds;
	};

	/**
	 * The Admin ROLE is the super administrator and gets every permission (#500).
	 *
	 * Not the retired `is_admin` column: that used to hand a flagged property manager this
	 * entire block — reports, materials, the admin area, every delete — when the only thing
	 * anyone meant by it was "can also manage user accounts". That is the `user_admin`
	 * capability now, granted narrowly in the role branch below.
	 */
	if (user?.role === UserRole.Admin) {
		return {
			// Navigation permissions
			// Demo mode users don't see Reports or Users (they're local-only)
			canViewReports: !isDemoMode,
			canViewUsers: !isDemoMode,
			canViewMaterials: true,
			canViewAdmin: !isDemoMode, // Hide admin section in demo mode
			canViewInspections: false, // Disabled for everyone

			// Association permissions
			canEditAssociations: true,
			canCreateAssociations: true,
			canDeleteAssociations: true,
			canEditAssociationNotes: true,

			// Work order permissions
			canEditWorkOrders: true,
			canCreateWorkOrders: true,
			canDeleteWorkOrders: true,
			canDeleteCostLines: true,
			isWorkOrderAdmin: true,
			canManageOwnerContact: true,

			// Exterior applications — admins get the full matrix, on web only.
			canUseInbox,

			// User management permissions
			canEditUsers: !isDemoMode,
			canCreateUsers: !isDemoMode,
			canDeleteUsers: !isDemoMode,
			canManageOthersTasks: true,

			// Admin permissions
			canAccessAdmin: !isDemoMode,
			// The super administrator holds every capability by definition.
			hasAdminCapability: !isDemoMode,
			canManageDB: !isDemoMode,
			// Not gated on demo mode: it edits nothing outside D1 and demo users have their own
			// local database, so the folder editor is safe to show.
			canManageDocumentStructure: true,
			isDocumentOperator: true,
			canUpdateAccounts: true,
			isAssociationAdmin: true,
			// Settings page is available in demo mode (for logout/delete account)
			canViewSettings: true,

			// The super administrator can both review and approve, so both queues show.
			canViewReviewQueue: true,
			canViewApprovalQueue: true,

			// Special flags
			isAdmin: true,
			isPropertyManager: false,
			isSupport: false,
			isMaintenance: false,
			isContractor: false,
			isAccounting: false,
			isLoading: flagsLoading,

			// Helper methods
			getAssignedAssociationIds,
		};
	}

	// Role-based permissions for non-admin users
	const role = user?.role;
	const isContractor = role === UserRole.Contractor;
	// User administration as a named capability: the Users page and its edit modal, nothing
	// else. The admin AREA (SQL, backups, restore) stays with the Admin role above.
	const isUserAdmin = hasFlag(FeatureFlagName.UserAdmin) && !isContractor;
	/**
	 * The other two things the retired `is_admin` column used to carry, now named (#500 follow-up).
	 *
	 * Both mirror `CAPABILITY_PERMISSIONS` on the server, so the control that offers the action
	 * and the route that performs it read the same concept. Both default to FALSE while
	 * `/me/feature-flags` is in flight — `isLoading` below is how a route guard waits — which is
	 * safe in exactly one direction: a button appears late. No consumer of these redirects or
	 * removes anything on a false, so nothing bounces a flagged user mid-load.
	 */
	const isAssociationAdmin = hasFlag(FeatureFlagName.AssociationAdmin) && !isContractor;
	const isWorkOrderAdmin = hasFlag(FeatureFlagName.WorkOrderAdmin) && !isContractor;
	/**
	 * Association notes: the EXACT server rule in `PUT /associations/:id`, which needs
	 * `UPDATE_ASSOCIATIONS` for the request at all and then writes `notes` only for a property
	 * manager or an `UPDATE_ACCOUNTS` holder. Both permissions are mirrored from the matrix:
	 * `UPDATE_ASSOCIATIONS` is denied only to Support and Contractor rows (and restored by
	 * `association_admin`); `UPDATE_ACCOUNTS` is the Accounting role or `accounting_admin` WITH
	 * `accounting`, like `useAccountingPermissions().canModify`.
	 */
	const holdsUpdateAssociations = isAssociationAdmin
		|| role === UserRole.PropertyManager || role === UserRole.Maintenance || role === UserRole.Accounting;
	const holdsUpdateAccounts = role === UserRole.Accounting
		|| (hasFlag(FeatureFlagName.AccountingAdmin) && hasFlag(FeatureFlagName.Accounting));
	const canEditAssociationNotes = holdsUpdateAssociations
		&& (role === UserRole.PropertyManager || holdsUpdateAccounts);

	return {
		// Navigation permissions
		/**
		 * Every role but contractors (2.6.0 follow-up). This used to be PM + Support only,
		 * which locked accounting out of the work-orders report they use to reconcile billing.
		 * The report's API is gated on VIEW_WORK_ORDERS, which every non-contractor already
		 * holds, so the page was the only thing hiding it. Contractors stay out: the report is
		 * portfolio-wide and their read scope is their own assigned work.
		 */
		canViewReports: !isContractor,
		canViewUsers: isUserAdmin,
		/**
		 * The material-cost catalog. Every non-contractor role holds `VIEW_MATERIAL_COSTS` (and
		 * create/update) on the server — property managers included — so hiding the page from
		 * everyone but Accounting refused a screen whose API answers. Contractors stay out: their
		 * reads are scoped to their assigned work orders.
		 */
		canViewMaterials: !isContractor,
		canViewAdmin: false, // Hidden for all non-admin roles
		canViewInspections: false, // Disabled for everyone

		/**
		 * Associations: read-only unless the person holds the `association_admin` capability.
		 *
		 * Hard DELETE is NOT part of it and stays `false` for every non-admin role — an
		 * unrecoverable removal is the Admin role's alone. Archiving, which is what the office
		 * actually does with a community that has left, rides on `canEditAssociations`: the
		 * archive button is an association UPDATE that cascades to the work orders.
		 */
		canEditAssociations: isAssociationAdmin,
		canCreateAssociations: isAssociationAdmin,
		canDeleteAssociations: false,
		canEditAssociationNotes,

		// Work order permissions — contractors can only edit their own assigned ones
		// (enforced server-side); they cannot create new work orders.
		canEditWorkOrders: true,
		canCreateWorkOrders: !isContractor,
		/**
		 * Delete: the `work_order_admin` capability, OR a role that already holds the permission.
		 *
		 * Both halves are load-bearing. The capability half is what gives a property manager or
		 * a maintenance user delete, which their matrix row denies. The role half is what stops
		 * this gate being STRICTER than the server: Accounting and Support hold
		 * `DELETE_WORK_ORDERS` outright, so gating them on a flag would hide a button whose
		 * request succeeds — and would take working functionality away from every unflagged
		 * accounting and support user, since only `is_admin = 1` holders get the flag by
		 * migration.
		 *
		 * Contractors are excluded by both halves: `isWorkOrderAdmin` is `&& !isContractor`, and
		 * their role is not in the list.
		 */
		canDeleteWorkOrders: isWorkOrderAdmin || ROLES_WITH_WORK_ORDER_DELETE.includes(role),
		// Same verdicts as work-order delete, role by role — see the interface docblock.
		canDeleteCostLines: isWorkOrderAdmin || ROLES_WITH_WORK_ORDER_DELETE.includes(role),
		isWorkOrderAdmin,
		// Every non-contractor role carries the narrow owner-contact write.
		canManageOwnerContact: !isContractor,

		// The Inbox — the per-user feature flag, web only, never for contractors.
		// Finer-grained rules (who may decide, who may delete) are enforced server-side.
		canUseInbox: canUseInbox && !isContractor,

		// User management: the user_admin capability, and only that.
		canEditUsers: isUserAdmin,
		canCreateUsers: isUserAdmin,
		canDeleteUsers: isUserAdmin,
		canManageOthersTasks: isUserAdmin,

		// Admin permissions (hidden for all non-admin)
		canAccessAdmin: false,
		/**
		 * The one admin concept a non-admin CAN hold. Backup / Restore / SQL stay behind
		 * `canAccessAdmin` above; the read-only operational tools follow this.
		 *
		 * FALSE while `/me/feature-flags` is in flight, like its three sources — safe in the
		 * one direction that matters here, because no consumer redirects on a false; the
		 * sidebar just paints the rows a moment late.
		 */
		hasAdminCapability: isUserAdmin || isAssociationAdmin || isWorkOrderAdmin,
		canManageDB: false,
		// MANAGE_DOCUMENT_STRUCTURE is false for every role row and granted by association_admin.
		canManageDocumentStructure: isAssociationAdmin,
		// Both sources already exclude contractors, as `hasDocumentOperatorCapability` does.
		isDocumentOperator: isUserAdmin || isAssociationAdmin,
		canUpdateAccounts: holdsUpdateAccounts,
		isAssociationAdmin,
		// Settings is available to every role — it hosts per-user/device preferences
		// (e.g. envelope rotation), the signature editor, and sign-out. Admin-only
		// surfaces inside it (Organization, Data Management) stay gated separately.
		canViewSettings: true,

		// Review is accounting's job; approval belongs to everyone who is not on the tools.
		canViewReviewQueue: role === UserRole.Accounting,
		canViewApprovalQueue: !isContractor && role !== UserRole.Maintenance,

		// Special flags
		isAdmin: false,
		isPropertyManager: role === UserRole.PropertyManager,
		isSupport: role === UserRole.Support,
		isMaintenance: role === UserRole.Maintenance,
		isContractor,
		isAccounting: role === UserRole.Accounting,
		isLoading: flagsLoading,

		// Helper methods
		getAssignedAssociationIds,
	};
}

/**
 * The client mirror of `isAdminOrFlagged` (api/admin/feature-flags.ts) for the per-user WORDING
 * capabilities — `application_rules_admin`, `email_templates_admin`, `cheat_sheet_admin`.
 *
 * The Admin ROLE passes without the flag; anyone else needs the flag AND the
 * `VIEW_EXTERIOR_APPLICATIONS` floor every one of those routes passes, which every role but
 * Contractor holds. Not the retired `is_admin` column (#500): the server stopped honouring it,
 * so trusting it here offers a flagless holder an editor whose every save 403s, and hides it
 * from every Admin created since.
 *
 * `known` is false only while `/me/feature-flags` is in flight for someone the role alone
 * cannot answer for — the moment a guard that redirects or says "read-only" must wait on.
 */
export function useAdminOrFlagged(flag: FeatureFlagName): { allowed: boolean; known: boolean } {
	const { user } = useApi();
	const { hasFlag, isLoading } = useFeatureFlags();
	const isAdmin = user?.role === UserRole.Admin;
	return {
		allowed: isAdmin || (hasFlag(flag) && user?.role !== UserRole.Contractor),
		known: isAdmin || !isLoading,
	};
}
